import React, { useContext, useState } from "react";
import { useAlert } from "../../../AlertContext";
import { AuthContext } from "../../../AuthContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LoadingCircle from "../../../LoadingCircle";
import { DashboardContext, Team } from "../../DashboardContext";
import { PlatformContext } from "../../../Platform/PlatformContext";
import { UserContext } from "../../../UserContext";

const server_url = process.env.REACT_APP_BACKEND_URL;

type TeamManageCardProps = {
  myTeam: Team;
};

const TeamManageCard: React.FC<TeamManageCardProps> = ({ myTeam }) => {
  const { showAlert } = useAlert();
  const { role, session, setSession } = useContext(AuthContext);
  const {
    loadRequests,
    getPlayersFromTeam,
    getTeamJoinRequests,
    getCaptainsFromTeam,
  } = useContext(DashboardContext);
  const { user } = useContext(UserContext);

  const isAdmin = role === "ADMIN";
  const isCaptain = role === "CAPTAIN";
  const isMainCaptain = role === "CAPTAIN" && myTeam.captain.id === user?.id;
  const isCaptainPending = role === "CAPTAIN_PENDING";

  const navigate = useNavigate();
  const platform = useContext(PlatformContext);
  const isMobile = !platform?.isDesktop;

  // Editing / UI states
  const [editing, setEditing] = useState(false);
  const [teamManageCardLoading, setTeamManageCardLoading] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [showMember, setShowMember] = useState(false);
  const [captainChangeLoading, setCaptainChangeLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: myTeam.name || "",
    name_short: myTeam.name_short || "",
    accepted: myTeam.accepted,
    is_active: myTeam.is_active,
  });

  // Expand/collapse each player / captain
  const [expandedPlayers, setExpandedPlayers] = useState<{
    [playerId: number]: boolean;
  }>({});
  const [expandedCaptains, setExpandedCaptains] = useState<{
    [playerId: number]: boolean;
  }>({});

  const togglePlayerDetails = (playerId: number) => {
    setExpandedPlayers((prevState) => ({
      ...prevState,
      [playerId]: !prevState[playerId],
    }));
  };

  const toggleCaptainDetails = (playerId: number) => {
    setExpandedCaptains((prevState) => ({
      ...prevState,
      [playerId]: !prevState[playerId],
    }));
  };

  /* --------------------------- SERVER INTERACTIONS --------------------------- */

  // Accept Team (Admin only)
  const acceptTeam = async (team_id: number, captain_id: number) => {
    try {
      const response = await axios.post(`${server_url}/acceptTeam`, {
        team_id,
        access_token: session.access_token,
        captain_id,
      });
      if (response.data.status) {
        await loadRequests();
        showAlert("success", "Team accepted!");
      } else {
        showAlert("error", response.data.error);
      }
    } catch (error) {
      console.error("Error accepting team:", error);
      showAlert("error", "Unexpected Error when accepting Team!");
    }
  };

  // Accept or Reject Player
  const handleAccept = async (user_id: number, accept: boolean) => {
    try {
      setAcceptLoading(true);
      const response = await axios.post(`${server_url}/acceptPlayer`, {
        user_id,
        access_token: session.access_token,
        accept,
      });
      if (response.data.status) {
        showAlert("success", "Success!");
        await loadRequests();
      } else {
        showAlert("error", response.data.error);
      }
    } catch (error) {
      console.error("Error handleAccept:", error);
      showAlert("error", "Error handleAccept!");
    } finally {
      setAcceptLoading(false);
    }
  };

  // Remove player from team
  const removePlayer = async (player_id: number, player_name: string) => {
    if (
      !window.confirm(
        `Are you sure you want to remove player ${player_name} from the team?`
      )
    ) {
      return;
    }
    try {
      setRemoveLoading(true);
      const response = await axios.post(`${server_url}/removePlayerFromTeam`, {
        access_token: session.access_token,
        player_id: player_id,
      });
      if (response.data.status) {
        showAlert("success", "Player removed from team successfully!");
        await loadRequests();
      } else {
        showAlert("error", response.data.error);
      }
    } catch (error) {
      console.error("Error removing player:", error);
      showAlert("error", "Unexpected error when removing player!");
    } finally {
      setRemoveLoading(false);
    }
  };

  const updateCaptainStatus = async (
    target_id: number,
    make_captain: boolean,
    target_name: string
  ) => {
    const action = make_captain ? "promote" : "degrade";
    if (
      !window.confirm(
        `Are you sure you want to ${action} ${target_name} ${
          make_captain ? "to Co-Captain" : "to Player"
        }?`
      )
    )
      return;

    try {
      setCaptainChangeLoading(true);
      const response = await axios.post(`${server_url}/updateCaptainStatus`, {
        access_token: session.access_token,
        player_id: target_id,
        make_captain,
      });
      if (response.data.status) {
        showAlert("success", "Role updated!");
        await loadRequests();
      } else {
        showAlert("error", response.data.error);
      }
    } catch (err) {
      console.error("Error updateCaptainStatus:", err);
      showAlert("error", "Unexpected error when updating role!");
    } finally {
      setCaptainChangeLoading(false);
    }
  };

  /* -------------------------------- HANDLERS -------------------------------- */

  // Toggle the "Members" portion
  const handleExtendToggle = () => {
    setShowMember(!showMember);
  };

  // Editing logic
  const handleEditToggle = () => {
    setEditing(!editing);
    if (!editing) {
      setFormData({
        name: myTeam.name || "",
        name_short: myTeam.name_short || "",
        accepted: myTeam.accepted,
        is_active: myTeam.is_active,
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const updatedTeamData: Team = {
      ...myTeam,
      ...formData,
    };
    await updateTeamData(updatedTeamData);
  };

  const updateTeamData = async (updatedTeamData: Team) => {
    try {
      setTeamManageCardLoading(true);
      const response = await axios.post(`${server_url}/updateTeam`, {
        team_id: updatedTeamData.team_id,
        access_token: session.access_token,
        name: updatedTeamData.name,
        name_short: updatedTeamData.name_short,
        accepted: updatedTeamData.accepted,
        is_active: updatedTeamData.is_active,
      });
      if (response.data.status) {
        await loadRequests();
        showAlert("success", "Team data updated successfully!");
        setEditing(false);
      } else {
        showAlert("error", response.data.error);
      }
    } catch (error) {
      console.error("Error updating team data:", error);
      showAlert("error", "Unexpected error when updating team data!");
    } finally {
      setTeamManageCardLoading(false);
    }
  };

  // Delete team
  const deleteTeam = async () => {
    if (
      window.confirm(
        "Are you sure you want to delete this team? This action cannot be undone."
      )
    ) {
      try {
        setTeamManageCardLoading(true);
        const response = await axios.post(`${server_url}/deleteTeam`, {
          team_id: myTeam.team_id,
          access_token: session.access_token,
        });
        if (response.data.status) {
          showAlert(
            "success",
            "Team deleted successfully! Please log in again!"
          );
          if (role === "ADMIN") {
            await loadRequests();
          } else {
            setSession(null);
            navigate("/login");
          }
        } else {
          showAlert("error", response.data.error);
        }
      } catch (error) {
        console.error("Error deleting team:", error);
        showAlert("error", "Unexpected error when deleting the team!");
      } finally {
        setTeamManageCardLoading(false);
      }
    }
  };

  /* --------------------------------- RENDER --------------------------------- */

  return (
    <div className="flex flex-col bg-white/10 rounded-xl shadow-md p-6 mb-4 gap-y-6">
      <div className="flex">
        {/* -------------------------------- LEFT COLUMN ------------------------------- */}
        <div className="flex-1">
          {editing ? (
            /* ------------------------------ EDITING FORM ------------------------------ */
            <form onSubmit={handleSubmit} className="space-y-6 flex flex-col">
              <div>
                <label htmlFor="name" className="block font-medium">
                  Team Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white border text-zinc-900 placeholder-zinc-500 rounded-xl focus:outline-none focus:shadow-[0_0_10px_black]"
                />
              </div>

              <div>
                <label htmlFor="name_short" className="block font-medium">
                  Acronym
                </label>
                <input
                  type="text"
                  id="name_short"
                  name="name_short"
                  value={formData.name_short}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white border text-zinc-900 placeholder-zinc-500 rounded-xl focus:outline-none focus:shadow-[0_0_10px_black]"
                />
              </div>

              <div className="flex flex-row gap-x-8">
                {isAdmin && myTeam.accepted && (
                  <div>
                    <label htmlFor="accepted" className="block font-medium">
                      Accepted
                    </label>
                    <input
                      type="checkbox"
                      id="accepted"
                      name="accepted"
                      checked={formData.accepted}
                      onChange={handleCheckboxChange}
                      className="mt-1"
                    />
                  </div>
                )}

                {isAdmin && myTeam.accepted && (
                  <div>
                    <label htmlFor="is_active" className="block font-medium">
                      Active
                    </label>
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleCheckboxChange}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>

              <div className="grow"></div>

              {teamManageCardLoading ? (
                <LoadingCircle />
              ) : (
                <>
                  <div className="flex space-x-4 mt-4">
                    <button
                      type="submit"
                      className="flex-1 py-2 px-4 rounded-xl font-semibold text-white bg-green-500 hover:bg-green-600"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={handleEditToggle}
                      className="flex-1 py-2 px-4 rounded-xl font-semibold bg-white text-red-500 border-2 border-red-500 hover:bg-red-500 hover:text-white"
                    >
                      Cancel
                    </button>
                  </div>

                  {(isAdmin || isCaptainPending) && (
                    <button
                      type="button"
                      onClick={deleteTeam}
                      className="flex-1 py-2 px-4 rounded-xl font-semibold bg-red-500 text-white hover:bg-red-600 mt-4"
                    >
                      Delete Team
                    </button>
                  )}
                </>
              )}
            </form>
          ) : (
            /* ------------------------------- READ-ONLY ------------------------------- */
            <div className="flex flex-col h-full">
              <h3 className="text-xl font-bold mb-2">{myTeam.name}</h3>
              <p>Acronym: {myTeam.name_short}</p>
              <p>Team ID: {myTeam.team_id}</p>
              <p>Captain ID: {myTeam.captain ? myTeam.captain.id : -1}</p>
              <p>
                Created on: {new Date(myTeam.created_at).toLocaleDateString()}
              </p>
              <div className="grow"></div>
              <div className="flex space-x-4 mt-4">
                {isAdmin && !myTeam.accepted && (
                  <button
                    onClick={() =>
                      acceptTeam(
                        myTeam.team_id,
                        myTeam.captain ? myTeam.captain.id : -1
                      )
                    }
                    className="flex-1 py-2 px-4 bg-green-500 text-white rounded-xl hover:bg-green-600"
                  >
                    Accept Team
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleEditToggle}
                  className="flex-1 py-2 px-4 rounded-xl font-semibold text-white bg-blue-500 hover:bg-blue-600"
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ------------------------------ RIGHT COLUMN ------------------------------ */}
        <div className="h-auto flex flex-col items-center justify-center gap-y-3 px-4">
          <div className="bg-white p-2 rounded-xl w-full flex items-center justify-center">
            <img
              src={myTeam.logo_url}
              alt={`${myTeam.name_short} Logo`}
              className="w-32 h-32 object-contain"
            />
          </div>
          <div className="grow"></div>
          {myTeam.accepted ? (
            <div className="py-2 px-4 rounded-xl font-semibold text-green-500 border-2 border-green-500  bg-white w-full">
              Accepted
            </div>
          ) : (
            <div className="py-2 px-4 rounded-xl font-semibold text-orange-500 border-2 border-orange-500  bg-white w-full">
              Pending
            </div>
          )}
          {myTeam.is_active && (
            <div className="py-2 px-4 rounded-xl font-semibold text-green-500 border-2 border-green-500  bg-white w-full">
              Active
            </div>
          )}
          {!myTeam.is_active && myTeam.accepted && (
            <div className="py-2 px-4 rounded-xl font-semibold text-orange-500 border-2 border-orange-500  bg-white w-full">
              Not Active
            </div>
          )}
        </div>
      </div>

      {/* ------------------------- EXPANDABLE MEMBERS AREA -------------------------- */}
      <button
        className="w-full flex items-center bg-white/10 rounded-xl  justify-center hover:bg-white/20"
        onClick={handleExtendToggle}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className={`size-6 transition-transform duration-200 ${
            showMember ? "rotate-180" : ""
          }`}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {showMember && (
        <div className="flex flex-col">
          {/* ---------------------------- JOIN REQUESTS ---------------------------- */}
          <h3 className="text-xl font-bold">Join-Requests</h3>
          {getTeamJoinRequests(myTeam.team_id).map((player) => (
            <div
              key={player.id}
              className="flex flex-wrap p-2 rounded-xl border border-white bg-white/10 justify-between items-center mb-2"
            >
              <div className="flex flex-col">
                <p className="font-bold">
                  {player.id}: {player.name}
                </p>
                {player.mail && <p className="">Email: {player.mail}</p>}
                <p className="">Phone: {player.phone}</p>
              </div>

              {acceptLoading ? (
                <LoadingCircle />
              ) : (
                <div className="flex gap-x-2">
                  <button
                    onClick={() => handleAccept(player.id, true)}
                    className="flex-1 py-2 px-4 rounded-xl bg-green-500 text-white hover:bg-green-600"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => handleAccept(player.id, false)}
                    className="flex-1 py-2 px-4 rounded-xl bg-red-500 text-white hover:bg-red-600"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}

          {/* ------------------------------- CAPTAINS -------------------------------- */}
          <h3 className="text-xl font-bold mt-4 mb-2">
            Captain{getCaptainsFromTeam(myTeam.team_id).length > 1 ? "s" : ""}
          </h3>
          <div
            className={`grid ${
              isMobile ? "grid-cols-1" : "grid-cols-2"
            } gap-4 items-start`}
          >
            {getCaptainsFromTeam(myTeam.team_id).map((captain) => {
              const isExpanded = expandedCaptains[captain.id] || false;
              return (
                <div
                  key={captain.id}
                  className="flex flex-col w-full rounded-xl border border-white bg-white/10"
                >
                  {/* ---------- Kopfzeile ---------- */}
                  <div className="flex items-center px-2 py-1">
                    <p
                      className="font-bold flex-1 truncate"
                      title={`${role === "ADMIN" ? `${captain.id}: ` : ""}${
                        captain.name
                      }`}
                    >
                      {role === "ADMIN" && <span>{captain.id}: </span>}
                      {captain.name}
                    </p>
                    <button
                      onClick={() => toggleCaptainDetails(captain.id)}
                      className="ml-2 text-blue-500"
                      style={{
                        transition: "transform 0.3s ease",
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▼
                    </button>
                  </div>

                  {/* ---------- Details ---------- */}
                  {isExpanded && (
                    <div className="px-2 pb-2">
                      {captain.mail && <p>Email: {captain.mail}</p>}
                      <p>Phone: {captain.phone}</p>

                      {/* -------- Degrade-Button  -------- */}
                      {isMainCaptain && captain.id !== user?.id && (
                        <div className="mt-2">
                          {captainChangeLoading ? (
                            <LoadingCircle />
                          ) : (
                            <button
                              onClick={() =>
                                updateCaptainStatus(
                                  captain.id,
                                  false,
                                  captain.name
                                )
                              }
                              className="w-full py-2 px-4 rounded-xl bg-orange-500 text-white hover:bg-orange-600"
                            >
                              Degrade to Player
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* --------------------------- PLAYERS ------------------------------ */}
          <h3 className="text-xl font-bold mt-4 mb-2">Player</h3>
          <div
            className={`grid ${
              isMobile ? "grid-cols-1" : "grid-cols-2"
            } gap-4 items-start`}
          >
            {getPlayersFromTeam(myTeam.team_id).map((player) => {
              const isExpanded = expandedPlayers[player.id] || false;
              return (
                <div
                  key={player.id}
                  className="flex flex-col w-full rounded-xl border border-white bg-white/10"
                >
                  {/* ---------- Kopfzeile ---------- */}
                  <div className="flex items-center px-2 py-1">
                    <p
                      className="font-bold flex-1 truncate"
                      title={`${role === "ADMIN" ? `${player.id}: ` : ""}${
                        player.name
                      }`}
                    >
                      {role === "ADMIN" && <span>{player.id}: </span>}
                      {player.name}
                    </p>
                    <button
                      onClick={() => togglePlayerDetails(player.id)}
                      className="ml-2 text-blue-500"
                      style={{
                        transition: "transform 0.3s ease",
                        transform: isExpanded
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      }}
                    >
                      ▼
                    </button>
                  </div>

                  {/* ---------- Details ---------- */}
                  {isExpanded && (
                    <div className="px-2 pb-2">
                      {player.mail && <p>Email: {player.mail}</p>}
                      <p>Phone: {player.phone}</p>

                      {/* -------- Promote-Button  -------- */}
                      {isMainCaptain && (
                        <div className="mt-2">
                          {captainChangeLoading ? (
                            <LoadingCircle />
                          ) : (
                            <button
                              onClick={() =>
                                updateCaptainStatus(
                                  player.id,
                                  true,
                                  player.name
                                )
                              }
                              className="w-full py-2 px-4 rounded-xl bg-green-500 text-white hover:bg-green-600"
                            >
                              Promote to Co-Captain
                            </button>
                          )}
                        </div>
                      )}

                      {/* -------- Remove-Button  -------- */}
                      {(isCaptain || isAdmin) && (
                        <div className="mt-2">
                          {removeLoading ? (
                            <LoadingCircle />
                          ) : (
                            <button
                              onClick={() =>
                                removePlayer(player.id, player.name)
                              }
                              className="w-full py-2 px-4 rounded-xl bg-red-500 text-white hover:bg-red-600"
                            >
                              Remove from Team
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManageCard;
